/**
 * InitiateRenewalDialog (C.3 Commit 4 · Renewal Lifecycle)
 *
 * Creates a Draft proposal renewing the source AMC schedule. Operator
 * can optionally override the forward window (default: source.end_date+1
 * day → +1 year) and attach remarks. Backend stamps prior_amc_id +
 * renewal_proposal_id on both directions for bidirectional lineage.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowsClockwise, CheckCircle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { DialogFooter } from "./ui/dialog";
import FormDialog, { FormDialogCancel } from "./FormDialog";
import { toast } from "sonner";
import { annualPeriodEndIso } from "../lib/servicePeriod";

const addOneDay = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
};

export default function InitiateRenewalDialog({ open, onOpenChange, sched, onInitiated }) {
    const navigate = useNavigate();
    const [newStart, setNewStart] = useState("");
    const [newEnd, setNewEnd] = useState("");
    const [remarks, setRemarks] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const defaultStart = useMemo(() => addOneDay((sched?.end_date || "").slice(0, 10)), [sched]);
    const defaultEnd = useMemo(() => annualPeriodEndIso(defaultStart), [defaultStart]);
    const effectiveStart = newStart || defaultStart;
    const effectiveEnd = newEnd || defaultEnd;
    const periodValid = effectiveStart && effectiveEnd && effectiveEnd > effectiveStart;

    useEffect(() => {
        if (open) {
            setNewStart("");
            setNewEnd("");
            setRemarks("");
        }
    }, [open, sched?.id]);

    const onConfirm = async () => {
        if (!sched || !periodValid) return;
        setSubmitting(true);
        try {
            const r = await api.post(`/billing/amc/${sched.id}/initiate-renewal`, {
                new_start_date: effectiveStart,
                new_end_date: effectiveEnd,
                remarks: remarks.trim() || undefined,
            });
            toast.success(`Renewal proposal ${r.data.renewal_proposal_number} created`);
            if (onInitiated) onInitiated(r.data);
            onOpenChange(false);
            if (r.data.renewal_proposal_id) {
                navigate(`/proposals/${r.data.renewal_proposal_id}`);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    if (!sched) return null;

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            title="Initiate AMC Renewal"
            description={
                <>
                    Create a Draft proposal renewing this AMC. Line items, GST
                    treatment, and billing frequency are copied from the source
                    schedule. The new proposal is linked back to the source AMC
                    via <code>prior_amc_id</code> for lineage tracking.
                </>
            }
            contentClassName="max-w-2xl"
            testId={`initiate-renewal-dialog-${sched.id}`}
        >
            <div className="space-y-4">
                {/* Source AMC context */}
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="renewal-source-context">
                    <div className="text-xs uppercase tracking-wide text-slate-500">Source AMC</div>
                    <div className="mt-1 text-sm font-medium text-slate-900">
                        {sched.amc_name || sched.product_name}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-600">
                        {sched.client?.name} · {sched.billing_frequency} · ending{" "}
                        <strong>{(sched.end_date || "").slice(0, 10)}</strong>
                    </div>
                </div>

                {/* Forward window */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                        <Label className="text-xs font-medium uppercase tracking-wide text-slate-600">
                            New start date
                        </Label>
                        <Input
                            type="date"
                            value={newStart || defaultStart}
                            onChange={(e) => setNewStart(e.target.value)}
                            data-testid="renewal-new-start-input"
                        />
                        <div className="mt-1 text-[11px] text-slate-500">
                            Default: source end date + 1 day
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-medium uppercase tracking-wide text-slate-600">
                            New end date
                        </Label>
                        <Input
                            type="date"
                            value={newEnd || defaultEnd}
                            onChange={(e) => setNewEnd(e.target.value)}
                            data-testid="renewal-new-end-input"
                        />
                        <div className="mt-1 text-[11px] text-slate-500">Default: one year — ends the day before the anniversary</div>
                    </div>
                </div>
                {!periodValid && (
                    <div className="text-xs text-rose-600" data-testid="renewal-period-error">
                        New end date must be strictly after the new start date.
                    </div>
                )}

                {/* Remarks */}
                <div>
                    <Label className="text-xs font-medium uppercase tracking-wide text-slate-600">
                        Remarks <span className="text-slate-400">(optional)</span>
                    </Label>
                    <Textarea
                        rows={2}
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="e.g. Renewal for next 12-month support cycle."
                        data-testid="renewal-remarks-input"
                    />
                </div>

                {/* Outcome preview */}
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-900">
                    <div className="flex items-start gap-2">
                        <ArrowsClockwise size={14} className="mt-0.5 shrink-0" />
                        <div>
                            On confirm: a Draft proposal will be created for{" "}
                            <strong>{sched.client?.name}</strong> covering{" "}
                            <strong>{effectiveStart}</strong> →{" "}
                            <strong>{effectiveEnd}</strong>. You'll be taken to it
                            immediately to review/edit before sending.
                        </div>
                    </div>
                </div>
            </div>

            <DialogFooter className="mt-6">
                <FormDialogCancel disabled={submitting} testId="renewal-cancel-button">
                    Cancel
                </FormDialogCancel>
                <Button
                    type="button"
                    onClick={onConfirm}
                    disabled={!periodValid || submitting}
                    data-testid="renewal-confirm-button"
                >
                    <CheckCircle size={14} className="mr-2" />
                    {submitting ? "Creating…" : "Create renewal proposal"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}
